import type React from "react";
import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type translations from "../../translations/translations.json";
import PasswordInput from "../common/PasswordInput";
import { validatePassword, doPasswordsMatch } from "../../utils/passwordValidation";
import { usePasswordVisibility } from "../../hooks/usePasswordVisibility";
import LoadingState from "../common/LoadingState";

type TranslationKeys = keyof typeof translations.en;

interface AdminResetPasswordModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  onSubmit: (userId: string, newPassword: string) => Promise<void>;
  error: string | null;
}

const AdminResetPasswordModal: React.FC<AdminResetPasswordModalProps> = ({
  open,
  onClose,
  userId,
  onSubmit,
  error,
}) => {
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const { t } = useTranslation();

  const { visibilityStates, toggleVisibility } = usePasswordVisibility({
    initialStates: {
      newPassword: false,
      confirmPassword: false,
    },
  });

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewPassword(e.target.value);
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
  };

  const validateForm = (): boolean => {
    const passwordValidation = validatePassword(newPassword);
    const passwordsMatch = doPasswordsMatch(newPassword, confirmPassword);

    const errors: string[] = [...passwordValidation.errors];
    if (!passwordsMatch) {
      errors.push("Passwords do not match");
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      await onSubmit(userId, newPassword);
      setNewPassword("");
      setConfirmPassword("");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent>
          <LoadingState message="Resetting password..." />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{t("Reset Password" as TranslationKeys)}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {validationErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" component="div">
                {validationErrors.map((error) => (
                  <div key={error}>{t(error as TranslationKeys)}</div>
                ))}
              </Typography>
            </Alert>
          )}

          <PasswordInput
            label="New Password"
            name="newPassword"
            value={newPassword}
            onChange={handleNewPasswordChange}
            showPassword={visibilityStates.newPassword}
            setShowPassword={() => toggleVisibility("newPassword")}
            disabled={loading}
            sx={{ mb: 2 }}
          />

          <PasswordInput
            label="Confirm New Password"
            name="confirmPassword"
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            showPassword={visibilityStates.confirmPassword}
            setShowPassword={() => toggleVisibility("confirmPassword")}
            disabled={loading}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t("Cancel" as TranslationKeys)}</Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading || !newPassword || !confirmPassword}
          >
            {loading ? <CircularProgress size={24} /> : t("Reset Password" as TranslationKeys)}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AdminResetPasswordModal;
